import { useAsObservableSource, useLocalStore } from 'mobx-react-lite';

import { IcChannelIcon, IcPlusIcon, IcUsersIcon } from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { IPopoverMenuItem } from '@gem/ui-kit';

import Tasks from 'APP/Tasks';
import { CreateSpaceChannelPopup } from 'APP/main/PopupManager/Popups/CreateSpaceChannelPopup/CreateSpaceChannelPopup';
import { CreateSpaceGroupPopup } from 'APP/main/PopupManager/Popups/CreateSpaceGroupPopup/CreateSpaceGroupPopup';
import { MoveGroupsToSpacePopup } from 'APP/main/PopupManager/Popups/MoveGroupsToSpacePopup/MoveGroupsToSpacePopup';
import { Space } from 'STORE/Spaces/Space';

interface ISpacePanelActionsButtonPresenter {
  menuItems: IPopoverMenuItem[];
  canCreateAndMoveGroups: boolean;
  moveGroups(): void;
  newGroupChat(): void;
  newChannel(): void;
}

export function useSpacePanelActionsButtonPresenter(
  space: Space
): ISpacePanelActionsButtonPresenter {
  const { t } = useTranslation();
  const source = useAsObservableSource({ space });

  const presenter = useLocalStore<ISpacePanelActionsButtonPresenter>(() => ({
    get menuItems(): IPopoverMenuItem[] {
      return [
        {
          key: 'move-groups',
          title: t('space_add_group_channel_button'),
          icon: IcPlusIcon,
          isVisible: presenter.canCreateAndMoveGroups,
          onClick: presenter.moveGroups,
        },
        {
          key: 'new-group-chat',
          title: t('new_group_chat'),
          icon: IcUsersIcon,
          isVisible: presenter.canCreateAndMoveGroups,
          onClick: presenter.newGroupChat,
        },
        {
          key: 'new-channel',
          title: t('new_channel'),
          icon: IcChannelIcon,
          isVisible: presenter.canCreateAndMoveGroups,
          onClick: presenter.newChannel,
        },
      ].filter((item) => item.isVisible);
    },

    get canCreateAndMoveGroups(): boolean {
      return source.space.isOwner || source.space.isAdmin;
    },

    moveGroups(): void {
      Tasks.app.openPopup(MoveGroupsToSpacePopup, {
        params: { spaceId: source.space.id },
      });
    },

    newGroupChat(): void {
      Tasks.app.openPopup(CreateSpaceGroupPopup, {
        params: { spaceId: source.space.id },
      });
    },

    newChannel(): void {
      Tasks.app.openPopup(CreateSpaceChannelPopup, {
        params: { spaceId: source.space.id },
      });
    },
  }));

  return presenter;
}
